import {useMemo} from "react";
import {TypeOf} from "zod";
import {useAccessToken} from "../auth";
import {useClientId} from "../ClientIdProvider";
import {useTenantId} from "../tenant";
import {
	BookingShareCreation,
	BookingShareMutation,
	createApiClientWithBaseConfig,
	get_QueryBookingShares,
} from "./client";

export function useBookingShareApi() {
	const accessToken = useAccessToken();
	const tenantId = useTenantId();
	const clientId = useClientId();
	return useMemo(() => createBookingShareApi(tenantId, clientId, accessToken), [tenantId, accessToken]);
}

export type BookingShareQuery = TypeOf<get_QueryBookingShares["parameters"]>["query"];

function createBookingShareApi(tenantId: string, clientId: string | undefined, accessToken: string | undefined) {
	const {api, withConfig} = createApiClientWithBaseConfig(clientId, accessToken);
	return {
		createBookingShare: (bookingId: number, bookingShareCreation: BookingShareCreation) =>
			api.post(
				"/api/tenants/{tenantId}/bookings/{bookingId}/shares",
				withConfig({
					path: {tenantId, bookingId},
					body: bookingShareCreation,
				}),
			),
		updateBookingShare: (bookingId: number, bookingShareId: number, bookingShareMutation: BookingShareMutation) =>
			api.put(
				"/api/tenants/{tenantId}/bookings/{bookingId}/shares/{bookingShareId}",
				withConfig({
					path: {tenantId, bookingId, bookingShareId},
					body: bookingShareMutation,
				}),
			),
		deleteBookingShare: (bookingId: number, bookingShareId: number) =>
			api.delete(
				"/api/tenants/{tenantId}/bookings/{bookingId}/shares/{bookingShareId}",
				withConfig({
					path: {tenantId, bookingId, bookingShareId},
				}),
			),
		getBookingShare: (bookingShareId: number, bookingId: number) =>
			api.get(
				"/api/tenants/{tenantId}/bookings/{bookingId}/shares/{bookingShareId}",
				withConfig({
					path: {tenantId, bookingId, bookingShareId},
				}),
			),
		queryBookingShares: (bookingId: number, query: BookingShareQuery) =>
			api.get(
				"/api/tenants/{tenantId}/bookings/{bookingId}/shares",
				withConfig({
					path: {tenantId, bookingId},
					query,
				}),
			),
		countBookingShares: (bookingId: number) =>
			api.get(
				"/api/tenants/{tenantId}/bookings/{bookingId}/shares/count",
				withConfig({
					path: {tenantId, bookingId},
				}),
			),
	};
}
